import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { SHOPIFY_PRODUCT_TAG, SHOPIFY_PRODUCT_NAME } from '../../config';
import { findProductVariant } from '../../utils';
import { useRootSelector } from '../../store';

import {
  selectCartItem,
  selectItemProperties,
  selectSlots
} from '../item/store/itemSlice';
import { useFontsAndFields } from '../fonts/TextAndFont.hooks';
import { useIconsAndLabels } from '../iconAndLabel/IconAndLabelHooks';
import { useMaterials } from '../clothingType/Materials.hooks';
import { useGetProductQuery } from '../api';
import { useLabelShape } from '../LabelShapes/LabelShapeHooks';

import {
  addShopifyCartItem,
  buildPrimaryApiCartItem,
  buildUpsellApiCartItem,
  getQuantityProperty,
  removeShopifyCartItem,
  sendApiCartItem,
  updateApiCartItem,
  updateShopifyCartItem
} from './utils';
import { UpsellProduct } from '../upsells/types';
import { LabelSlot, Material } from '../../types';

export function useAddBaseProductToCart(
  productTag?: string,
  cartItemId?: string
) {
  const properties = useSelector(selectItemProperties);
  const primaryCartItemProperties = useSelector(selectCartItem);
  const { fonts, hideFontsAndFields } = useFontsAndFields();
  const { showIcons, labelHasMultipleVariants } = useIconsAndLabels();
  const { showLabelShapes } = useLabelShape();
  const { materials } = useMaterials();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );
  const slots = useRootSelector(selectSlots);

  const addBaseProduct = useCallback(async (): Promise<{
    success: boolean;
    productName?: string;
    cartItemId?: string;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    const primaryApiCartItem = buildPrimaryApiCartItem(properties, apiProduct);

    let primaryCartItemId;
    try {
      if (cartItemId) {
        primaryCartItemId = await updateApiCartItem(
          cartItemId,
          primaryApiCartItem
        );
      } else {
        primaryCartItemId = await sendApiCartItem(primaryApiCartItem);
      }
    } catch (error) {
      console.error('Error adding item to API cart:', error);
      return { success: false, error: 'Error adding to cart' };
    }

    // Build Shopify cart properties
    const {
      Font,
      ['Clothing Type']: clothingType,
      Icon,
      Style,
      Palette,
      ...otherPrimaryCartItemProperties
    } = primaryCartItemProperties;

    const fieldNames: { [key: string]: string | undefined } = {};

    const hasNameField = otherPrimaryCartItemProperties.Name;
    if (hasNameField) {
      fieldNames.Name = otherPrimaryCartItemProperties.Name;
      delete otherPrimaryCartItemProperties.Name;
    }

    const hasFullNameField = otherPrimaryCartItemProperties['Full Name'];
    if (hasFullNameField) {
      fieldNames['Full Name'] = otherPrimaryCartItemProperties['Full Name'];
      delete otherPrimaryCartItemProperties['Full Name'];
    }

    if (!hideFontsAndFields) {
      Object.entries(otherPrimaryCartItemProperties).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          fieldNames[key] = value;
          delete otherPrimaryCartItemProperties[key];
        }
      });
    }

    const userProperties: { [key: string]: string | undefined } = {
      Style,
      Color: Palette
    };

    if (fonts?.length > 1) {
      userProperties.Font = Font;
    }

    if (showIcons) {
      userProperties.Icon = Icon;
    }

    const shapes = [];
    if (showLabelShapes && properties.labelShape?.display_name) {
      shapes.push(properties.labelShape.display_name);
    }
    apiProduct?.label_slots?.forEach(labelSlot => {
      const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;
      const label = apiProduct.labels.find(l => l.label_id === labelId);
      const isIterative = label?.iterative_style_ids?.includes(
        properties?.style?.style_id ?? ''
      );
      const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];

      if (hasMultipleVariants && !isIterative) {
        const variantId =
          properties.slots[labelSlot.product_label_slot_id].variantId;
        const variant = label?.variants.find(
          v => v.label_variant_id === variantId
        );
        if (variant?.system_name) shapes.push(variant.system_name);
      }
    });

    if (shapes.length) {
      userProperties.Shape = shapes.join(', ');
    }

    if (materials?.length > 1) {
      userProperties['Clothing Type'] = clothingType;
    }

    const quantity = getQuantityProperty(properties.slots, apiProduct);

    const formattedItemProperties = {
      Labels: quantity.toString(),
      ...fieldNames,
      ...userProperties,
      ...otherPrimaryCartItemProperties,
      _order_system: 'NEW',
      _cart_item_id: primaryCartItemId,
      _product_id: apiProduct?.product_id,
      _style_id: properties.style?.style_id,
      _palette_id: properties.palette?.palette_id,
      _font_id: properties.font?.font_id,
      _icon_id: properties.icon?.icon_id,
      _material_id: properties.clothingType?.material_id
    };

    const { labelShape, palette, style } = properties;
    const productVariant = findProductVariant(labelShape, palette, style);

    const primaryProductItem = {
      id: productVariant?.id,
      quantity: 1,
      properties: formattedItemProperties
    };

    if (cartItemId) {
      const params = new URLSearchParams(window.location.search);
      const key = params.get('key')!;
      const [variantId, _hash] = key.split(':');

      if (Number(variantId) !== primaryProductItem.id) {
        await removeShopifyCartItem(key);
        await addShopifyCartItem(primaryProductItem);
      } else {
        await updateShopifyCartItem(key, primaryProductItem);
      }
    } else {
      const response = await addShopifyCartItem(primaryProductItem);

      if (!response.ok) {
        const json = await response.json();
        console.error('error adding to cart', json);
        return { success: false, error: 'Error adding to cart' };
      }
    }

    const productName = apiProduct.display_name || SHOPIFY_PRODUCT_NAME;
    return { success: true, productName, cartItemId: primaryCartItemId };
  }, [
    properties,
    apiProduct,
    primaryCartItemProperties,
    hideFontsAndFields,
    fonts?.length,
    showIcons,
    showLabelShapes,
    materials?.length,
    cartItemId,
    slots,
    labelHasMultipleVariants
  ]);

  return addBaseProduct;
}

export function useAddUpsellToCart(
  upsell: UpsellProduct, // UpsellProduct type from upsells feature
  selectedMaterial: Material, // Material type
  baseProductCartItemId?: string,
  productTag?: string
) {
  const properties = useSelector(selectItemProperties);
  const { fonts } = useFontsAndFields();
  const { showIcons } = useIconsAndLabels();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  const addUpsell = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    try {
      // Build primary cart item for reference
      const primaryApiCartItem = buildPrimaryApiCartItem(
        properties,
        apiProduct
      );

      // Build upsell cart item
      const chosenUpsell = { upsell, material: selectedMaterial };
      const upsellApiCartItemData = buildUpsellApiCartItem(
        chosenUpsell,
        properties,
        primaryApiCartItem
      );

      // Send to API
      const upsellCartItemId = await sendApiCartItem(upsellApiCartItemData);

      // Build Shopify cart properties for upsell
      const upsellProperties: { [key: string]: string } = {};

      // Calculate label quantity
      const labelQuantity = upsell.label_slots.reduce(
        (acc: number, slot: LabelSlot) => {
          const { slot_options } = slot;
          const [option] = slot_options;
          return (acc += option?.quantity ?? 0);
        },
        0
      );

      upsellProperties.Labels = labelQuantity.toString();

      // Add text fields
      const { fields } = properties;
      const configuredTextFields = Object.values(fields);

      // Add Name or Full Name first
      const prioritizedDisplayNames = ['Name', 'Full Name'];
      const prioritizedTextField = configuredTextFields.find(field =>
        prioritizedDisplayNames.includes(field.display_name!)
      );

      if (prioritizedTextField) {
        const { display_name, value } = prioritizedTextField;
        upsellProperties[display_name!] = value;
      }

      // Add other text fields
      for (const { display_name, value } of configuredTextFields) {
        const isAlreadyAdded =
          prioritizedTextField &&
          display_name === prioritizedTextField.display_name;

        if (isAlreadyAdded) continue;

        upsellProperties[display_name!] = value;
      }

      // Add style, color, font, icon
      const { style, palette, font, icon } = properties;

      upsellProperties.Style = style!.display_name;
      upsellProperties.Color = palette!.display_name;

      if (fonts?.length > 1 && font) {
        upsellProperties.Font = font.display_name;
      }

      if (showIcons && icon) {
        upsellProperties.Icon = icon.display_name;
      }

      // Add material
      if (selectedMaterial?.display_name) {
        upsellProperties['Clothing Type'] = selectedMaterial.display_name;
      }

      // Add system properties
      upsellProperties._order_system = 'NEW';
      upsellProperties._cart_item_id = upsellCartItemId;

      // Link upsell to base product
      if (baseProductCartItemId) {
        upsellProperties._parent_item_id = baseProductCartItemId;
      }

      // Add metadata IDs
      if (properties.style?.style_id)
        upsellProperties._style_id = properties.style.style_id;
      if (properties.palette?.palette_id)
        upsellProperties._palette_id = properties.palette.palette_id;
      if (properties.font?.font_id)
        upsellProperties._font_id = properties.font.font_id;
      if (properties.icon?.icon_id)
        upsellProperties._icon_id = properties.icon.icon_id;
      if (selectedMaterial?.material_id)
        upsellProperties._material_id = selectedMaterial.material_id;

      // Add to Shopify cart
      const response = await addShopifyCartItem({
        id: upsell.shopify_id!,
        quantity: 1,
        properties: upsellProperties
      });

      if (!response.ok) {
        const json = await response.json();
        console.error('error adding upsell to cart', json);
        return { success: false, error: 'Error adding upsell to cart' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error adding upsell to cart:', error);
      return { success: false, error: 'Error adding upsell to cart' };
    }
  }, [
    upsell,
    selectedMaterial,
    properties,
    apiProduct,
    baseProductCartItemId,
    fonts?.length,
    showIcons
  ]);

  return addUpsell;
}
